import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginCredentials, RegisterData } from '../types';
import { authService } from '../services/authService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<any>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (token) {
          // Verify token and get user profile
          const userProfile = await authService.getProfile();
          setUser(userProfile);
        }
      } catch (error) {
        // Token is invalid, clear it
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Set up token refresh timer
  useEffect(() => {
    if (!user) return;

    const refreshInterval = setInterval(async () => {
      try {
        await refreshToken();
      } catch (error) {
        console.error('Failed to refresh token:', error);
        logout();
      }
    }, 14 * 60 * 1000); // Refresh every 14 minutes (tokens expire at 15 minutes)

    return () => clearInterval(refreshInterval);
  }, [user]);

  const login = async (credentials: LoginCredentials) => {
    try {
      setIsLoading(true);
      console.log('FRONTEND: Starting login process...');
      
      let response;
      try {
        console.log('FRONTEND: Calling authService.login...');
        response = await authService.login(credentials);
        console.log('FRONTEND: Login service call successful, response:', response);
      } catch (loginError: any) {
        console.error('FRONTEND: Login service call failed:', loginError);
        console.error('FRONTEND: Login error details:', {
          message: loginError.message,
          response: loginError.response?.data,
          status: loginError.response?.status
        });
        // Preserve the original error structure for better error handling in LoginPage
        throw loginError;
      }
      
      // Store tokens
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken || '');
      
      // Get user profile
      let userProfile;
      try {
        console.log('FRONTEND: Calling authService.getProfile...');
        userProfile = await authService.getProfile();
        console.log('FRONTEND: Profile service call successful, user:', userProfile);
      } catch (profileError: any) {
        console.error('FRONTEND: Profile service call failed:', profileError);
        console.error('FRONTEND: Profile error details:', {
          message: profileError.message,
          response: profileError.response?.data,
          status: profileError.response?.status
        });
        // Preserve the original error structure
        throw profileError;
      }
      
      setUser(userProfile);
    } catch (error) {
      console.error('FRONTEND: Overall login process failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterData) => {
    try {
      setIsLoading(true);
      const response = await authService.register(data);
      
      // Always store tokens and log the user in automatically after registration
      if (response.accessToken) {
        localStorage.setItem('accessToken', response.accessToken);
        localStorage.setItem('refreshToken', response.refreshToken || '');
        
        // Get user profile and log them in
        const userProfile = await authService.getProfile();
        setUser(userProfile);
      }
      
      return response;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Clear tokens and user state
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    
    // Call logout endpoint to invalidate server-side session
    authService.logout().catch(console.error);
  };

  const refreshToken = async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refreshToken');
      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }

      const response = await authService.refreshToken(refreshTokenValue);
      
      // Update stored tokens
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      
      return response;
    } catch (error) {
      // Refresh failed, logout user
      logout();
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (token) {
        const userProfile = await authService.getProfile();
        setUser(userProfile);
      }
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
      throw error;
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    refreshToken,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}